import { AlertCircle, Loader } from 'lucide-react';
import { type ReactNode } from 'react';

import { type SessionSummary } from '../../services/api/sessions';

import { SessionCard } from './SessionCard';

const Notice = ({ children }: { children: ReactNode }) => (
  <div className="p-8 text-center rounded-lg border border-slate-200 bg-slate-50">
    <p className="text-slate-600">{children}</p>
  </div>
);

interface SessionListProps {
  isLoading: boolean;
  error: Error | null;
  unavailableReason: string | null;
  sessions: SessionSummary[];
  visible: SessionSummary[];
  selectedId?: string;
  onSelect: (session: SessionSummary) => void;
}

export const SessionList = ({
  isLoading,
  error,
  unavailableReason,
  sessions,
  visible,
  selectedId,
  onSelect,
}: SessionListProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-red-900">Fel vid hämtning</h3>
          <p className="text-sm text-red-800 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <Notice>
        {unavailableReason
          ? 'Sessioner kunde inte laddas från backend just nu.'
          : 'Inga sessioner i vald period'}
      </Notice>
    );
  }

  if (visible.length === 0) {
    return <Notice>Inga sessioner matchar sökningen</Notice>;
  }

  return (
    <div className="space-y-3 max-h-[800px] overflow-y-auto">
      {visible.map((session) => (
        <SessionCard
          key={session.sessionId}
          session={session}
          onSelect={onSelect}
          isSelected={selectedId === session.sessionId}
        />
      ))}
    </div>
  );
};
